import React, { useState, useMemo, useEffect } from "react";
import {
  FaDownload,
  FaRegStopCircle,
  FaCopy,
  FaListAlt,
  FaClock,
  FaFileContract,
} from "react-icons/fa";
import { LuSpeech } from "react-icons/lu";
import html2pdf from "html2pdf.js";
import { Document, Packer, Paragraph, TextRun } from "docx";
import { toast } from "react-toastify";
import ReactMarkdown from "react-markdown";
import remarkGFM from "remark-gfm";
import ModalComponent from "../../../../components/ModalComponent";

const SummarySidebar = ({ summary, summaries, loading, isOpen, onToggle }) => {
  // Local UI states
  const [expandedSections, setExpandedSections] = useState({});
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [activeTab, setActiveTab] = useState("summary");

  const synth = window.speechSynthesis;

  useEffect(() => {
    return () => {
      if (synth.speaking) synth.cancel();
    };
  }, []);

  /** ──────────────── SUMMARY PARSING ──────────────── */
  const parseSummary = (summaryText) => {
    if (!summaryText) return [];

    if (summaryText.includes("<") && summaryText.includes(">")) {
      return [
        {
          title: "Agreement Details",
          content: summaryText,
          isHtml: true,
          items: [],
        },
      ];
    }

    const sections = [];
    const lines = summaryText.split("\n").filter((line) => line.trim());
    let currentSection = null;

    lines.forEach((line) => {
      const trimmed = line.trim();
      if (trimmed.startsWith("*") || trimmed.startsWith("-")) {
        if (currentSection) {
          currentSection.items.push(trimmed.replace(/^[\*\-]\s*/, ""));
        }
      } else if (trimmed.includes(":") && !trimmed.startsWith("*")) {
        if (currentSection) sections.push(currentSection);
        const [title, content] = trimmed.split(":");
        currentSection = {
          title: title.replace(/\*\*/g, "").trim(),
          content: content.trim(),
          items: [],
          isHtml: false,
        };
      }
    });

    if (currentSection) sections.push(currentSection);

    return sections.length > 0
      ? sections
      : [
          {
            title: "Summary",
            content: summaryText,
            items: [],
            isHtml: false,
          },
        ];
  };

  const sections = useMemo(() => parseSummary(summary), [summary]);
  const isHtmlContent = useMemo(
    () => sections.some((s) => s.isHtml),
    [sections]
  );

  useEffect(() => {
    if (isHtmlContent && sections.length > 0) {
      const allExpanded = {};
      sections.forEach((_, index) => {
        allExpanded[index] = true;
      });
      setExpandedSections(allExpanded);
    }
  }, [isHtmlContent, sections]);

  /** ──────────────── TEXT CLEANING ──────────────── */
  const getPlainText = () => {
    if (!summary) return "";
    return summary.replace(/[*_#`<>]+/g, "").replace(/\n\n/g, "\n");
  };

  /** ──────────────── COPY / SPEECH / DOWNLOAD ──────────────── */
  const handleCopySummary = () => {
    const plainText = getPlainText();
    if (!plainText) {
      toast.warning("No content to copy");
      return;
    }
    navigator.clipboard
      .writeText(plainText)
      .then(() => toast.success("Summary copied to clipboard!"))
      .catch(() => toast.error("Failed to copy summary"));
  };

  const handleSpeechToggle = () => {
    if (isSpeaking) {
      synth.cancel();
      setIsSpeaking(false);
    } else if (summary) {
      synth.cancel();
      const plainText = getPlainText();
      if (!plainText) {
        toast.error("No content available to read");
        return;
      }

      const chunks = plainText.match(/.{1,200}/g) || [plainText];
      let index = 0;
      const speakChunk = () => {
        if (index >= chunks.length) {
          setIsSpeaking(false);
          synth.cancel();
          return;
        }
        const utterance = new SpeechSynthesisUtterance(chunks[index]);
        utterance.rate = 1;
        utterance.onend = () => {
          index++;
          speakChunk();
        };
        setIsSpeaking(true);
        synth.speak(utterance);
      };
      speakChunk();
    }
  };

  const handleStop = () => {
    if (synth.speaking) {
      synth.cancel();
      setIsSpeaking(false);
    }
  };

  const handleDownloadSummary = async (format) => {
    if (!summary) {
      toast.error("No content available to download");
      return;
    }

    try {
      const summaryContent = getPlainText();
      const filename = `agreement_summary.${format}`;

      if (format === "pdf") {
        const element = document.createElement("div");
        element.innerHTML = `
          <div>
            <h1 style="font-weight: bold; font-size: 18pt; margin-bottom: 20px; border-bottom: 1px solid #eee; padding-bottom: 10px;">
              Agreement Summary
            </h1>
            <div style="font-size: 12pt; line-height: 1.6; color: #333;">
              ${summaryContent.replace(/\n/g, "<br>")}
            </div>
          </div>
        `;
        const opt = {
          margin: 10,
          filename,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        };
        await html2pdf().from(element).set(opt).save();
      } else if (format === "docx") {
        const doc = new Document({
          sections: [
            {
              children: [
                new Paragraph({
                  text: "Agreement Summary",
                  heading: "Heading1",
                }),
                new Paragraph({ text: "" }),
                ...summaryContent
                  .split("\n")
                  .map(
                    (line) =>
                      new Paragraph({
                        children: [new TextRun({ text: line || "" })],
                      })
                  ),
              ],
            },
          ],
        });
        const blob = await Packer.toBlob(doc);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      toast.success(`Downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download content");
    }
  };

  /** ──────────────── TIMELINE PARSER ──────────────── */
  const parseTimelineData = (timelineText) => {
    if (!timelineText) return [];
    try {
      // If it's JSON, parse it
      return JSON.parse(timelineText);
    } catch {
      // fallback for simple text lines
      return timelineText
        .split("\n")
        .filter((line) => line.trim())
        .map((line) => {
          const [date, event] = line.split(":");
          return { date: date?.trim(), event: event?.trim() };
        });
    }
  };

  /** ──────────────── MODAL FOOTER ──────────────── */
  const modalFooterButtons = [
    {
      label: "Close",
      variant: "primary",
      onClick: onToggle,
    },
  ];

  /** ──────────────── RENDER ──────────────── */
  return (
    <ModalComponent
      show={isOpen}
      handleClose={onToggle}
      title="Agreement Summary"
      size="lg"
      footerButtons={modalFooterButtons}
    >
      <div className="signature-modal-body">
        {loading ? (
          <div className="signature-loader-wrapper">
            <div className="signature-loader"></div>
            <p className="signature-loader-text">Loading summary...</p>
          </div>
        ) : summaries ? (
          <div className="signature-content-wrapper">


            {/* Tab Content Area */}
            <div className="signature-tab-content">
              {activeTab === "summary" && (
                <div className="signature-content-section">
                  <div className="signature-markdown-display">
                    <ReactMarkdown remarkPlugins={[remarkGFM]}>
                      {summaries.contract_summary || "No summary available"}
                    </ReactMarkdown>
                  </div>
                </div>
              )}

            </div>
          </div>
        ) : (
          <div className="signature-empty-state">
            <p>No summary available.</p>
          </div>
        )}
      </div>
    </ModalComponent>
  );
};

export default SummarySidebar;
